import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, ExternalLink, GripVertical, Save } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const POSITION_OPTIONS = [
  { value: 'header', label: 'Header (Top Navigation)' },
  { value: 'footer', label: 'Footer (Bottom)' },
  { value: 'sidebar_left', label: 'Left Sidebar' },
  { value: 'sidebar_right', label: 'Right Sidebar' },
  { value: 'menu', label: 'Menu Tab' },
  { value: 'middle', label: 'Middle Section' }
];

const AdminImportantLinks = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    url: '',
    position: 'footer',
    is_active: true,
    order: 0,
    icon: ''
  });

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const response = await api.get('/admin/important-links');
      setLinks(response.data);
    } catch (error) {
      console.error('Failed to fetch links:', error);
      toast.error('Failed to load links');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLink) {
        await api.put(`/admin/important-links/${editingLink.id}`, formData);
        toast.success('Link updated successfully');
      } else {
        await api.post('/admin/important-links', formData);
        toast.success('Link created successfully');
      }
      setShowForm(false);
      setEditingLink(null);
      resetForm();
      fetchLinks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save link');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      url: '',
      position: 'footer',
      is_active: true,
      order: 0,
      icon: ''
    });
  };

  const handleEdit = (link) => {
    setEditingLink(link);
    setFormData({
      title: link.title,
      url: link.url,
      position: link.position || 'footer',
      is_active: link.is_active,
      order: link.order,
      icon: link.icon || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this link?')) return;
    try {
      await api.delete(`/admin/important-links/${id}`);
      toast.success('Link deleted');
      fetchLinks();
    } catch (error) {
      toast.error('Failed to delete link');
    }
  };

  const getPositionLabel = (pos) => {
    const option = POSITION_OPTIONS.find(o => o.value === pos);
    return option ? option.label : pos;
  };

  const getPositionColor = (pos) => {
    const colors = {
      header: 'bg-blue-500/20 text-blue-500',
      footer: 'bg-gray-500/20 text-gray-400',
      sidebar_left: 'bg-purple-500/20 text-purple-500',
      sidebar_right: 'bg-pink-500/20 text-pink-500',
      menu: 'bg-green-500/20 text-green-500',
      middle: 'bg-yellow-500/20 text-yellow-500'
    };
    return colors[pos] || 'bg-gray-500/20 text-gray-400';
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="admin-important-links-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold">Important Links</h1>
            <p className="text-muted-foreground mt-2">Manage links for different sections of your website</p>
          </div>
          <button
            onClick={() => { setShowForm(true); setEditingLink(null); resetForm(); }}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Link
          </button>
        </div>

        {/* Position Guide */}
        <div className="glass-card p-4 mb-6">
          <h3 className="font-semibold mb-3">Position Guide:</h3>
          <div className="flex flex-wrap gap-2">
            {POSITION_OPTIONS.map(opt => (
              <span key={opt.value} className={`px-3 py-1 rounded-full text-xs font-semibold ${getPositionColor(opt.value)}`}>
                {opt.label}
              </span>
            ))}
          </div>
        </div>

        {/* Links List */}
        <div className="glass-card p-6 space-y-4">
          {links.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No links added yet. Click "Add Link" to create one.
            </div>
          ) : (
            links.map((link) => (
              <div key={link.id} className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                <div className="flex items-center gap-4">
                  <GripVertical className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <h3 className="font-semibold flex items-center gap-2">
                      {link.title}
                      <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-primary">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </h3>
                    <p className="text-sm text-muted-foreground">{link.url}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getPositionColor(link.position)}`}>
                    {getPositionLabel(link.position)}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${link.is_active ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                    {link.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <button onClick={() => handleEdit(link)} className="p-2 bg-primary/20 text-primary rounded-lg">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(link.id)} className="p-2 bg-red-500/20 text-red-500 rounded-lg">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
            <div className="glass-card p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-6">{editingLink ? 'Edit Link' : 'Add New Link'}</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                    placeholder="e.g., Terms & Conditions"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">URL *</label>
                  <input
                    type="url"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                    placeholder="https://example.com/terms"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Display Position *</label>
                  <select
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  >
                    {POSITION_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Icon (optional)</label>
                  <input
                    type="text"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                    placeholder="e.g., FileText, Shield, etc."
                  />
                  <p className="text-xs text-muted-foreground mt-1">Use Lucide icon names</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Display Order</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                    placeholder="0"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <label htmlFor="is_active" className="text-sm">Active (visible on website)</label>
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="submit" className="flex-1 btn-primary flex items-center justify-center gap-2">
                    <Save className="w-4 h-4" />
                    {editingLink ? 'Update' : 'Create'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowForm(false); setEditingLink(null); resetForm(); }}
                    className="flex-1 px-4 py-3 bg-white/5 rounded-full font-semibold"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminImportantLinks;
